import { useAmp } from 'next/amp'
import { useCleanPostData } from '@features/post/contexts/CleanPostDataContext'
import { useTranslation } from '@i18n'
import { publicUrl } from '@libs/appUrl'
import getNewsArticle from '@libs/getNewsArticle'
import Head from 'next/head'
import { useRouter } from 'next/router'
import { getCoverImage } from '../libs/postTools'
import useOpenGraphTags from './useOpenGraphTags'

function PostHead() {
  const router = useRouter()
  const isAmp = useAmp()
  const { t, i18n } = useTranslation('nav')
  const {
    post,
    title: postTitle,
    plainTextContent: postDescription,
    url: postUrl,
    hashTags,
    tags,
  } = useCleanPostData()
  const { clas } = post
  const now = Date.now()
  const localePopdaily = t('common:波波黛莉')
  const suffix = `${t('common:PopDaily')} ${localePopdaily}`
  const publisherName = t('common:publisherName', { locales: t(`common:${i18n.language}`) })

  // canonical - nofollow rules
  const isUgc = post.type === 'user' // ugc
  const isCreator = post.newText && post.newText.length > 0 // oldText: Normal User newText: Creator
  const nofollow = isUgc && !isCreator

  // cover
  const coverImage = getCoverImage(post)

  const getTitle = () => {
    const title = postTitle || postDescription.slice(0, 40)

    if (!isUgc) return `${title}｜${suffix}`

    const name = t(`nav:${clas[0]}.name`)
    const category = t('nav:endsWithBoard', { name })

    return `${title}-${category}｜${suffix}`
  }

  // title and description for SEO
  const title = getTitle()
  const tagString = tags.slice(0, 10).join(',')
  // description 最多長度 150
  const description =
    postDescription.slice(0, 150 - 5 - tagString.length) + `相關標籤：${tagString}`

  const authorName = post.author || post.userInfo?.name || localePopdaily
  const datePublished = new Date(post.t || now).toISOString()
  const dateModified = new Date(post.ut || now).toISOString()

  const newsArticle = getNewsArticle({
    headline: title,
    description: postDescription.slice(0, 60),
    articleBody: postDescription,
    mainEntityOfPage: postUrl,
    datePublished,
    dateModified,
    keywords: hashTags,
    image: coverImage,
    author: { name: authorName },
    publisher: { name: publisherName, url: publicUrl() },
    thumbnailUrl: coverImage.url,
  })

  const type = isUgc ? 'explore' : 'website'
  const breadcrumbList = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'PopDaily 波波黛莉的異想世界 ',
        item: publicUrl(),
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: t(`nav:${type}.${clas[0]}.name`),
        item: publicUrl(isUgc ? `/explore/${clas[0]}` : `/${clas[0]}`),
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: title,
        item: postUrl,
      },
    ],
  }

  const clasPrefix = isUgc ? 'explore_' : 'website_'

  const ogTags = useOpenGraphTags()

  return (
    <Head>
      {/* 標準頁面：canonical 指向自己，amphtml 指向 AMP 版本 */}
      {/* AMP 頁面：canonical 指向標準版本，不顯示 amphtml */}
      {!isAmp ? (
        <>
          <link key="canonical" rel="canonical" href={postUrl} />
          <link rel="amphtml" href={`${postUrl}?amp=1`} />
        </>
      ) : (
        <link key="canonical" rel="canonical" href={postUrl} />
      )}
      {nofollow && <link key="nofollow" rel="nofollow" href={postUrl} />}
      <title key="title">
        {router.pathname.includes('draft') ? `預覽文章－${post.title}｜PopDaily 波波黛莉` : title}
      </title>
      <meta
        name="pubdate"
        itemProp="dateCreated"
        property="article:published_time"
        content={datePublished}
      />
      <meta key="news_keywords" name="news_keywords" content={tagString} />
      <meta name="lastmod" itemProp="dateModified" content={dateModified} />
      <meta key="description" name="description" content={description} />
      {ogTags}
      {/* NewsArticle */}
      <script
        key="NewsArticle"
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(newsArticle) }}
      />
      {/* BreadcrumbList */}
      <script
        key="BreadcrumbList"
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbList) }}
      />
      <meta property="article:published_time" content={datePublished} />
      <meta property="article:section" content={t(clasPrefix + clas[0])} />
    </Head>
  )
}

export default PostHead
